import { h, Component } from 'preact';

import Catalogue from './Catalogue';
import './index.less';

import {
  getCatalogues,
  getCataloguesPosition,
  getSortOrder,
  showCataloguesInGridView
} from '../../lib/GroupCatalogueAmalgam';
import { getCatalogueTheme } from '../../lib/Theme';
import { getScrollTop } from '../../lib/PositionCache';
import { isMobile, CUSTOM_PAGE_CATALOGUE_TILES_PER_ROW_DEFAULT } from '../../lib/Utils';
import {
  getCustomPage,
  getPictureForProduct,
  getPictureUrlForCatalogue
} from '../../lib/CustomPageAmalgam';
import { getPictureDominantColor } from '../../lib/Product';

let scrollCache = 0;
const catalogueStateCache = {};
let paginationScrollCache = 0;
const SORT_ORDER_ALPHABETICAL = 'ALPHABETICAL';
const GRID_SPACING_GAP = 20;
const GRID_TILE_MAX_HEIGHT = 500;

class CatalogueList extends Component {
  constructor (props) {
    super(props);
    const { sectionId, customPageSectionCataloguesList } = this.props;
    if (catalogueStateCache[sectionId]) {
      this.state = catalogueStateCache[sectionId];
    } else {
      const catalogues = {};
      let cataloguesList = [];
      if (customPageSectionCataloguesList) {
        cataloguesList = customPageSectionCataloguesList;
      } else {
        cataloguesList = getCatalogues();
      }
      cataloguesList = cataloguesList.filter((catalogue) => !!catalogue);
      cataloguesList.forEach((catalogue) => {
        catalogues[catalogue.id] = catalogue;
      });
      this.state = {
        catalogues,
        sortedCatalogueIds: this.getSortedCatalogueIds({
          catalogues
        })
      };
    }
    this.key = sectionId;
  }

  componentDidMount () {
    const { sectionId } = this.props;
    window.scrollTo(0, scrollCache);
    this.key = sectionId;
    catalogueStateCache[sectionId] = { ...this.state };
  }

  componentDidUpdate (previousProps, prevState) {
    if (
      prevState.sortedCatalogueIds.length !== this.state.sortedCatalogueIds.length &&
      scrollCache
    ) {
      window.scrollTo(0, scrollCache);
      return;
    }
    if (paginationScrollCache) {
      window.scrollTo(0, paginationScrollCache);
      paginationScrollCache = 0;
    }
  }

  componentWillUnmount () {
    scrollCache = getScrollTop();
    this.syncStateToCache();
  }

  syncStateToCache = () => {
    catalogueStateCache[this.key] = { ...this.state };
  };

  getSortedCatalogueIds = ({ catalogues }) => {
    const cataloguesPosition = getCataloguesPosition();
    if (!cataloguesPosition || !Object.keys(cataloguesPosition).length) {
      return Object.keys(catalogues);
    }
    const sortOrder = getSortOrder();

    return Object.keys(catalogues).sort((c1, c2) => {
      const c1Position = cataloguesPosition[c1];
      const c2Position = cataloguesPosition[c2];
      const catalogue1 = catalogues[c1];
      const catalogue2 = catalogues[c2];

      if (sortOrder === SORT_ORDER_ALPHABETICAL) {
        if (catalogue1.title && catalogue2.title) {
          return catalogue1.title.localeCompare(catalogue2.title);
        }

        if (catalogue1.title) {
          return -1;
        }

        if (catalogue2.title) {
          return 1;
        }
      }

      if (typeof c1Position === 'number' && typeof c2Position === 'number') {
        return c1Position - c2Position;
      }

      if (typeof c1Position === 'number') {
        return 1;
      }

      if (typeof c2Position === 'number') {
        return -1;
      }

      return new Date(catalogue2.dateUpdated) - new Date(catalogue1.dateUpdated);
    });
  };

  getCatalogueTilesPerRow = () => {
    const { sectionLayoutColumns = CUSTOM_PAGE_CATALOGUE_TILES_PER_ROW_DEFAULT } = this.props;
    const { sortedCatalogueIds = [] } = this.state;
    let catalogueTilesPerRow = 1;
    const mobileCatalogueTileColumns = 2;
    const desktopCatalogueTileColumns =
      typeof sectionLayoutColumns === 'number'
        ? sectionLayoutColumns
        : CUSTOM_PAGE_CATALOGUE_TILES_PER_ROW_DEFAULT;
    if (isMobile()) {
      if (sortedCatalogueIds.length > 1) {
        catalogueTilesPerRow = Math.min(mobileCatalogueTileColumns, sortedCatalogueIds.length);
      } else {
        catalogueTilesPerRow = 1;
      }
    } else if (sortedCatalogueIds.length === 1) {
      catalogueTilesPerRow = 1;
    } else if (sortedCatalogueIds.length > 1 && sortedCatalogueIds.length <= 5) {
      catalogueTilesPerRow = Math.min(desktopCatalogueTileColumns, sortedCatalogueIds.length);
    } else if (sortedCatalogueIds.length > 5) {
      catalogueTilesPerRow = Math.min(desktopCatalogueTileColumns, 5);
    }
    return catalogueTilesPerRow;
  };

  getCatalogueTileWidth = () => {
    const { sectionId } = this.props;
    const catalogueTilesContainer = document.querySelector(
      `.custom-page-catalogue-list.section-${sectionId}`
    );
    const catalogueTilesPerRow = this.getCatalogueTilesPerRow();
    const catalogueTileWidth =
      (catalogueTilesContainer ? catalogueTilesContainer.clientWidth : window.innerWidth) /
        catalogueTilesPerRow -
      GRID_SPACING_GAP / catalogueTilesPerRow;
    return catalogueTileWidth;
  };

  getCatalogueTileStyles = () => ({
    height: '100%',
    minHeight: '100px',
    width: `${this.getCatalogueTileWidth()}px`,
    minWidth: '100px',
    maxWidth: '100%'
  });

  getCatalogueImageStyles = () => ({
    height: '100%',
    maxHeight: `${GRID_TILE_MAX_HEIGHT}px`,
    width: `${this.getCatalogueTileWidth()}px`,
    maxWidth: '100%',
    objectFit: 'contain'
  });

  render () {
    const { catalogues, sortedCatalogueIds } = this.state;
    const {
      fromBundle,
      tabIndex,
      containerClass,
      sectionId,
      groupShowcaseId,
      hideCatalogueTitle = false,
      hideProductCount = false
    } = this.props;

    const catalogueTileWidth = this.getCatalogueTileWidth();
    const catalogueTilesPerRow = this.getCatalogueTilesPerRow();
    const productImageTileSize =
      catalogueTilesPerRow === 1
        ? 'FULL'
        : catalogueTilesPerRow === 2
          ? '1000'
          : catalogueTilesPerRow <= 4
            ? '600'
            : '400';
    const isGridLayout = showCataloguesInGridView();
    const customPage = getCustomPage();
    const { singleShowcases = [], products = [] } = customPage;

    const primaryColor = getCatalogueTheme().addToCartButton.backgroundColor;
    const catalogueTileStyles = this.getCatalogueTileStyles();
    const catalogueImageStyles = this.getCatalogueImageStyles();

    const totalCatalogueIds = [...sortedCatalogueIds];
    const catalogueTileRowIds = [];
    while (totalCatalogueIds.length) {
      catalogueTileRowIds.push(totalCatalogueIds.splice(0, catalogueTilesPerRow));
    }

    return (
      <div
        ref={(element) => (this.catalogueListElement = element)}
        className={`custom-page-catalogue-list section-${sectionId} ${
          isGridLayout ? 'custom-page-catalogue-grid-view' : ''
        } ${containerClass || ''}`}
        style={{ justifyContent: isGridLayout ? 'center' : undefined }}
      >
        {catalogueTileRowIds.map((catalogueIds, index) => {
          const catalogueTileMaxHeight = Math.max(
            ...catalogueIds.map((catalogueId) => {
              const { firstProductId } = catalogues[catalogueId];
              const product = products.find((product) => product.id === firstProductId) || {};
              const picture = getPictureForProduct({ product }) || {};
              let height = picture.height || 0;
              if (picture.height && picture.width) {
                const aspectRatio = picture.width / picture.height;
                height = catalogueTileWidth / aspectRatio;
              }
              return height;
            })
          );
          if (catalogueTileMaxHeight > GRID_SPACING_GAP) {
            catalogueImageStyles.height = catalogueTileMaxHeight - GRID_SPACING_GAP;
          }
          if (catalogueImageStyles.height > GRID_TILE_MAX_HEIGHT) {
            catalogueImageStyles.objectFit = 'cover';
          }
          return (
            <div className="custom-page-catalogue-row" key={index}>
              {catalogueIds.map((catalogueId, index) => {
                const { randomSlug, catalogueSlug, companySlug, encodedDomainTag } =
                  singleShowcases.find(
                    (singleShowCase) => singleShowCase.catalogueId === catalogueId
                  ) || {};
                const slug = `${companySlug}/${catalogueSlug}/${randomSlug}`;
                catalogues[catalogueId].slug = slug;
                catalogues[catalogueId].encodedDomainTag = encodedDomainTag;
                // NOTE: firstProductId here is actually the variantId
                // Variants are already merged with their parent products when custompage is loaded first time
                const { firstProductId, pictureUrl: cataloguePictureUrl } = catalogues[catalogueId];
                if (!cataloguePictureUrl && firstProductId) {
                  const product = products.find((product) => product.id === firstProductId) || {};
                  catalogues[catalogueId].pictureUrl = getPictureUrlForCatalogue({ product });
                  const picture = getPictureForProduct({ product });
                  if (!!picture && Object.keys(picture).length > 0) {
                    catalogues[catalogueId].pictureHeight = picture.height;
                    catalogues[catalogueId].pictureWidth = picture.width;
                    catalogues[catalogueId].dominantColor =
                      primaryColor || getPictureDominantColor(picture);
                  }
                }

                return (
                  <Catalogue
                    key={catalogueId}
                    {...catalogues[catalogueId]}
                    index={index}
                    fromBundle={fromBundle}
                    fromCustomPage
                    tabIndex={tabIndex}
                    sectionId={sectionId}
                    groupShowcaseId={groupShowcaseId}
                    catalogueTileStyles={catalogueTileStyles}
                    catalogueImageStyles={catalogueImageStyles}
                    productImageTileSize={productImageTileSize}
                    hideCatalogueTitle={hideCatalogueTitle}
                    hideProductCount={hideProductCount}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }
}

export default CatalogueList;
